import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton, makeStyles, Tooltip,
} from '@material-ui/core';
import {
  Download as DownloadIcon,
} from 'react-feather';
import { withNotification, withPermissionsChecker } from 'wrappers';

const useStyles = makeStyles(theme => ({
  icon: {
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
}));

const MASSIVE_EXPORT_MESSAGE = (
  'Su exportación masiva está en proceso. Pronto recibirá un correo con el link de descarga.'
);

/**
 * Component that renders the Excel download button, temporarily disables it while
 * the request is in progress, and notifies the user with a persistent Snackbar.
 *
 * @param {Object} props - Component properties
 * @param {Function} props.onClick - Callback executed when the button is clicked
 * @param {Array<string>} props.requiredPermissions - Permissions required to enable the button
 * @param {Function} props.checkAccess - Function to validate user permissions
 * @param {Function} props.appInfo - Notification function injected by withNotification
 * @param {boolean} props.disabled - External disabled state flag
 * @returns {React.ReactElement} The rendered Excel download IconButton
 */
const DownloadExcelButton = ({
  onClick,
  requiredPermissions = [],
  checkAccess,
  appInfo,
  disabled = false,
}) => {
  const classes = useStyles();
  const [isExporting, setIsExporting] = useState(false);

  /**
   * Handles the click event, disables the button while processing, and shows the persistent Snackbar.
   *
   * @returns {Promise<void>}
   */
  const handleDownloadClick = async () => {
    if (isExporting || disabled) {
      return;
    }

    setIsExporting(true);
    let queuedEventReceived = false;
    let customMessage = MASSIVE_EXPORT_MESSAGE;

    const handleQueuedEvent = (event) => {
      queuedEventReceived = true;
      if (event && event.detail && event.detail.message) {
        customMessage = event.detail.message;
      }
    };

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('erp:massive-export-queued', handleQueuedEvent);
    }

    try {
      const result = await onClick();
      const alreadyNotified = Boolean(result && result.notified);
      if (!alreadyNotified && queuedEventReceived && typeof appInfo === 'function') {
        appInfo(customMessage, { persist: true });
      }
    } finally {
      if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
        window.removeEventListener('erp:massive-export-queued', handleQueuedEvent);
      }
      setIsExporting(false);
    }
  };

  return (
    <IconButton
      className={classes.icon}
      onClick={handleDownloadClick}
      disabled={disabled || isExporting || !checkAccess(requiredPermissions)}
    >
      <Tooltip title="Descargar excel">
        <DownloadIcon className={classes.icon} fontSize="large" />
      </Tooltip>
    </IconButton>
  );
};

DownloadExcelButton.defaultProps = {
  requiredPermissions: [],
  disabled: false,
};

DownloadExcelButton.propTypes = {
  onClick: PropTypes.func,
  checkAccess: PropTypes.func,
  appInfo: PropTypes.func,
  requiredPermissions: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
};

export default withNotification(withPermissionsChecker(DownloadExcelButton));
